import React, { useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const Form = () => {
  const [fullname, setFullname] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { theme } = useTheme();
  const { t } = useTranslation();

  const getFormStyles = () => {
    if (theme === 'modern') {
      return 'modern-card flex gap-4 sm:gap-5 flex-col w-full max-w-sm sm:max-w-md lg:max-w-lg mx-auto p-4 sm:p-6 rounded-xl shadow-2xl';
    }
    return 'flex gap-4 sm:gap-5 flex-col w-full max-w-sm sm:max-w-md lg:max-w-lg mx-auto bg-white p-4 sm:p-6 border-3 sm:border-4 border-black rounded-lg shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transform hover:-translate-y-2 hover:-translate-x-2 hover:shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] transition-all duration-300';
  };

  const getInputStyles = () => {
    if (theme === 'modern') {
      return 'w-full rounded-xl border-2 border-gray-200 p-3 sm:p-4 font-medium shadow-sm outline-none transition-all duration-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:shadow-lg hover:shadow-md hover:border-gray-300 text-sm sm:text-base bg-white/80 backdrop-blur-sm placeholder:text-gray-400';
    }
    return 'w-full rounded-lg border-4 sm:border-5 border-black p-3 sm:p-4 font-bold shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] outline-none transition-all focus:translate-x-[3px] focus:translate-y-[3px] focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] sm:focus:translate-x-[4px] sm:focus:translate-y-[4px] sm:focus:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:bg-yellow-100 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] sm:hover:shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] active:translate-x-[3px] active:translate-y-[3px] active:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] text-sm sm:text-base bg-white transform-gpu';
  };

  const getTextareaStyles = () => {
    if (theme === 'modern') {
      return 'w-full h-20 sm:h-24 lg:h-28 resize-none rounded-xl border-2 border-gray-200 p-3 sm:p-4 font-medium shadow-sm outline-none transition-all duration-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:shadow-lg hover:shadow-md hover:border-gray-300 text-sm sm:text-base bg-white/80 backdrop-blur-sm placeholder:text-gray-400';
    }
    return 'w-full h-20 sm:h-24 lg:h-28 resize-none rounded-lg border-4 sm:border-5 border-black p-3 sm:p-4 font-bold shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] outline-none transition-all focus:translate-x-[3px] focus:translate-y-[3px] focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] sm:focus:translate-x-[4px] sm:focus:translate-y-[4px] sm:focus:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:bg-yellow-100 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] sm:hover:shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] active:translate-x-[3px] active:translate-y-[3px] active:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] text-sm sm:text-base bg-white transform-gpu';
  };

  const getButtonStyles = () => {
    if (theme === 'modern') {
      return `modern-button-primary w-full flex justify-center cursor-pointer items-center mt-2 sm:mt-3 text-sm sm:text-base font-semibold py-3 sm:py-4 px-6 rounded-lg hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl transition-all duration-300 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`;
    }
    return `w-full flex justify-center cursor-pointer items-center rounded-lg border-3 border-black bg-blue-400 px-6 py-3 sm:py-4 font-black text-sm sm:text-base shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none hover:bg-blue-500 active:translate-x-[1px] active:translate-y-[1px] transform-gpu ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`;
  };

  const showSuccessAlert = () => {
    toast.success(t('form.toast.success'), {
      autoClose: 5000,
      hideProgressBar: true,
    });
  };

  const showFailedAlert = (errorMessage = t('form.toast.failure')) => {
    toast.error(errorMessage, {
      autoClose: 5000,
      hideProgressBar: true,
    });
  };

  const handleSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post('https://simple-backend-message-api.vercel.app/api/sendmessage', {
        name: fullname,
        email,
        subject,
        msg: message
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000, // 10 second timeout
      });

      if (response.status === 200 || response.status === 201) {
        showSuccessAlert();
        // Clear form fields
        setFullname('');
        setEmail('');
        setSubject('');
        setMessage('');
      } else {
        showFailedAlert(t('form.toast.unexpected'));
      }
    } catch (error: any) {
      console.error('Error sending message:', error);

      if (error.code === 'ECONNABORTED') {
        showFailedAlert(t('form.toast.timeout'));
      } else if (error.response) {
        showFailedAlert(t('form.toast.serverError', { status: error.response.status }));
      } else if (error.request) {
        showFailedAlert(t('form.toast.networkError'));
      } else {
        showFailedAlert(t('form.toast.genericError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={getFormStyles()}>
      
      <div className="space-y-4 sm:space-y-5 lg:space-y-6">
        <input
          className={getInputStyles()}
          type="text"
          placeholder={t('form.placeholders.name')}
          value={fullname}
          onChange={ev => setFullname(ev.target.value)}
          aria-label="name"
          required
          disabled={isSubmitting}
        />
        <input
          className={getInputStyles()}
          type="email"
          placeholder={t('form.placeholders.email')}
          value={email}
          onChange={ev => setEmail(ev.target.value)}
          required
          disabled={isSubmitting}
        />
        <input
          className={getInputStyles()}
          type="text"
          placeholder={t('form.placeholders.subject')}
          value={subject}
          onChange={ev => setSubject(ev.target.value)}
          aria-label="subject"
          required
          disabled={isSubmitting}
        />
        <textarea
          className={getTextareaStyles()}
          placeholder={t('form.placeholders.message')}
          value={message}
          onChange={ev => setMessage(ev.target.value)}
          required
          disabled={isSubmitting}
        ></textarea>
      </div>
      
      <button
        type='submit'
        className={getButtonStyles()}
        disabled={isSubmitting}
      >
        <span>{isSubmitting ? t('form.button.loading') : t('form.button.default')}</span>
        {!isSubmitting && (
        <svg className="ml-2 w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
        )}
      </button>
    </form>
  );
};

export default Form;
